import React, { Children, useEffect, useMemo } from 'react';

import { Menu } from '@mui/material';

import {
    DEFAULT_MENU_ITEM_STYLES,
    DEFAULT_MENU_LIST_STYLES,
    DEFAULT_MENU_STYLES,
} from './customizedDropdownMenuStytles';
import classes from './customizedDropdownMenuTriggerElement.module.scss';

interface CustomizedDropdownProps {
    triggerElement;
    showDivider?: boolean;
    menuStyles?: any;
    menuItemStyles?: any;
    menuListStyles?: any;
    disabled?: boolean;
    closeDropdownOnItemClick?: boolean;
    children: React.ReactNode;
}

export const CustomizedDropdownMenu = ({
    triggerElement,
    children,
    menuItemStyles,
    menuStyles,
    menuListStyles,
    disabled = false,
    closeDropdownOnItemClick = true,
}: CustomizedDropdownProps) => {
    const [anchorEl, setAnchorEl] = React.useState(null);
    const arrayOfChildren = useMemo(() => {
        return Children.toArray(children);
    }, [children]);
    const open = Boolean(anchorEl);

    const handleItemClick = (originalOnClick) => (event) => {
        if (originalOnClick) {
            originalOnClick(event);
        }
        if (closeDropdownOnItemClick) {
            setAnchorEl(null);
        }
    };

    const childrenWithClickHandlers = useMemo(() => {
        if (!closeDropdownOnItemClick) {
            return arrayOfChildren;
        }
        return arrayOfChildren.map((child) => {
            if (React.isValidElement(child)) {
                return React.cloneElement(child, {
                    onClick: handleItemClick(child.props.onClick),
                } as any);
            }
            return child;
        });
    }, [arrayOfChildren, closeDropdownOnItemClick]);

    const handleOpen = (event) => {
        if (disabled) {
            return;
        }
        setAnchorEl(event.currentTarget);
    };

    const handleClose = (e) => {
        const clickOnInput = e.target instanceof HTMLInputElement;
        if (!clickOnInput) {
            setAnchorEl(null);
        }
        setAnchorEl(null);
    };

    const mergedItemStyles = { ...DEFAULT_MENU_ITEM_STYLES, ...menuItemStyles };
    const mergedMenuStyles = { ...DEFAULT_MENU_STYLES, ...menuStyles };
    const mergedListStyle = { ...DEFAULT_MENU_LIST_STYLES, ...menuListStyles };

    useEffect(() => {
        window.addEventListener('scroll', handleClose);
        return () => window.removeEventListener('scroll', handleClose);
    }, []);

    useEffect(() => {
        if (arrayOfChildren.length === 0) {
            setAnchorEl(null);
        }
    }, [arrayOfChildren]);

    return (
        <>
            <div
                data-testid={'customized-dropdown-menu'}
                className={classes.triggerElement}
                onClick={handleOpen}
                onKeyUp={handleOpen}
            >
                {triggerElement}
            </div>

            <Menu
                sx={{
                    '.MuiMenuItem-root': mergedItemStyles,
                    '.MuiMenu-paper': mergedMenuStyles,
                    '.MuiList-root': mergedListStyle,
                }}
                onClose={handleClose}
                anchorEl={anchorEl}
                open={open}
                disableAutoFocusItem
                disableEnforceFocus
                disableScrollLock
                closeAfterTransition={true}
            >
                {childrenWithClickHandlers}
            </Menu>
        </>
    );
};
